import React, { useEffect, useRef, useState } from "react";
import { useKT } from "../i18n/kt";
import "./karkhana-ui.scss";

/** "18:52" → hour "6", minute "52", "PM". Blank or bad → empty boxes, AM. */
export const splitTime = (value: string): { hour: string; minute: string; half: "AM" | "PM" } => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value || "");
  if (!m) return { hour: "", minute: "", half: "AM" };
  const h = Number(m[1]);
  return { hour: String(h % 12 === 0 ? 12 : h % 12), minute: m[2], half: h >= 12 ? "PM" : "AM" };
};

/** Hour 1–12, minute 0–59 and AM/PM → "HH:mm" (24 h), or "" while it isn't a whole time yet. */
export const joinTime = (hour: string, minute: string, half: "AM" | "PM"): string => {
  if (!/^\d{1,2}$/.test(hour) || !/^\d{1,2}$/.test(minute)) return "";
  const h = Number(hour);
  const min = Number(minute);
  if (h < 1 || h > 12 || min > 59) return "";
  const h24 = (h % 12) + (half === "PM" ? 12 : 0);
  return `${String(h24).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
};

/** "18:52" → "6:52 PM", as the field shows it. */
export const formatTime12 = (value: string): string => {
  const { hour, minute, half } = splitTime(value);
  return hour ? `${hour}:${minute} ${half}` : "--:--";
};

const digits = (raw: string) => raw.replace(/\D/g, "").slice(0, 2);

/**
 * A time as hour : minute boxes that open the phone's number pad, and AM/PM as a dropdown. The
 * phone's own clock picker was hard to use on the shop floor (owner, 2026-11-04). Gives "HH:mm".
 */
const TimeField = ({
  value,
  onChange,
  label,
  autoFocus,
}: {
  value: string;
  onChange: (hhmm: string) => void;
  label: string;
  autoFocus?: boolean;
}) => {
  const { t } = useKT();
  const start = splitTime(value);
  const [hour, setHour] = useState(start.hour);
  const [minute, setMinute] = useState(start.minute);
  const [half, setHalf] = useState<"AM" | "PM">(start.half);
  const minuteRef = useRef<HTMLInputElement>(null);

  // "Now" and other outside changes fill the boxes again; the person's own typing is left alone.
  useEffect(() => {
    if (value === joinTime(hour, minute, half)) return;
    const s = splitTime(value);
    setHour(s.hour);
    setMinute(s.minute);
    setHalf(s.half);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (h: string, m: string, x: "AM" | "PM") => onChange(joinTime(h, m, x));
  const select = (e: React.FocusEvent<HTMLInputElement>) => e.target.select();

  return (
    <span className="kt-time" role="group" aria-label={label}>
      <input
        className="kt-time__box"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        maxLength={2}
        placeholder="hh"
        value={hour}
        aria-label={t("Hour")}
        autoFocus={autoFocus}
        onFocus={select}
        onChange={(e) => {
          const h = digits(e.target.value);
          setHour(h);
          emit(h, minute, half);
          // Two digits, or one that can't start a bigger hour (2–9): on to the minutes.
          if (h.length === 2 || (h.length === 1 && Number(h) > 1)) minuteRef.current?.focus();
        }}
      />
      <span className="kt-time__colon" aria-hidden="true">
        :
      </span>
      <input
        ref={minuteRef}
        className="kt-time__box"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        maxLength={2}
        placeholder="mm"
        value={minute}
        aria-label={t("Minute")}
        onFocus={select}
        onChange={(e) => {
          const m = digits(e.target.value);
          setMinute(m);
          emit(hour, m, half);
        }}
        onBlur={() => {
          if (minute.length === 1) {
            const m = minute.padStart(2, "0");
            setMinute(m);
            emit(hour, m, half);
          }
        }}
      />
      <select
        className="kt-time__half"
        value={half}
        aria-label={t("AM or PM")}
        onChange={(e) => {
          const x = e.target.value === "PM" ? "PM" : "AM";
          setHalf(x);
          emit(hour, minute, x);
        }}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </span>
  );
};

export default TimeField;
